import { createContext, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { cx } from "class-variance-authority";
import { AnimatePresence, motion } from "motion/react";
import type { VariantProps } from "class-variance-authority";

import { ButtonGroup } from "../ButtonGroup/ButtonGroup";
import { Button, type buttonVariants } from "../Button/Button";

import * as styles from "./CurrencyInput.module.scss";

type QuickInputPopoverRootProps = {
  className?: string;
  isVisible?: boolean;
  children?: ReactNode;
  currencySymbol: string;
} & Pick<VariantProps<typeof buttonVariants>, "variant" | "size">;

type QuickInputPopoverOptionProps = {
  onChange: (value: number) => void;
  value: number;
  label?: string;
};

type QuickInputPopoverContext = {
  currencySymbol: string;
} & Pick<VariantProps<typeof buttonVariants>, "variant" | "size">;

const QuickInputPopoverContext = createContext<QuickInputPopoverContext>({
  variant: "primary",
  currencySymbol: "$",
  size: "default",
});

const Root = ({
  className,
  isVisible = true,
  variant,
  children,
  currencySymbol,
  size,
}: QuickInputPopoverRootProps) => {
  const buttonGroupRef = useRef<HTMLDivElement | null>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);

  useEffect(() => {
    if (!isVisible || !buttonGroupRef.current) return;
    if (!("IntersectionObserver" in window)) {
      setIsIntersecting(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsIntersecting(entry.isIntersecting);
      },
      { threshold: 0.5 },
    );

    observer.observe(buttonGroupRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isVisible]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={(e) => {
            e.preventDefault();
          }}
          className={cx(styles.quickInput, className, {
            [styles.blurred]: variant === "blurred",
          })}
        >
          <QuickInputPopoverContext.Provider
            value={{ currencySymbol, variant, size }}
          >
            <div ref={buttonGroupRef}>
              <ButtonGroup
                className={cx(styles.options, {
                  ...(variant && { [styles[variant]]: !!variant }),
                  [styles.disabled]: !isIntersecting,
                })}
              >
                {children}
              </ButtonGroup>
            </div>
          </QuickInputPopoverContext.Provider>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Option = ({ onChange, value, label }: QuickInputPopoverOptionProps) => {
  const { currencySymbol, variant, size } = useContext(
    QuickInputPopoverContext,
  );
  return (
    <Button variant={variant} onClick={() => onChange(value)} size={size}>
      {label ? label : `+${currencySymbol}${value}`}
    </Button>
  );
};

const QuickInputPopover = { Root, Option };

export { QuickInputPopover };
